/**
 * 밸런스 상수 (contracts/object-catalog.md의 BalanceConfig 계약).
 * Clarifications 확정값은 고정, 감각 상수(임펄스 등)는 플레이테스트 튜닝 대상.
 * 모든 밸런스 수치는 이 파일에만 존재해야 한다(하드코딩 금지).
 */
export interface BalanceConfig {
  comboWindowSec: number;
  comboMultipliers: number[];
  slowmo: { minCombo: number; durationSec: number; timeScale: number };
  debris: { globalCap: number; lifetimeSec: number };
  /** 릴리스 속도 → 던지기 임펄스 스케일(튜닝) */
  throwImpulseScale: number;
  /** 파괴 시 파편 산포 임펄스(튜닝). speedRef: 이 상대속도에서 스케일 1x, speedScaleMax: 상한 */
  fragmentImpulse: { base: number; jitter: number; up: number; speedRef: number; speedScaleMax: number };
  /** 세게 부술수록 점수 가산: 임팩트속도/임계값 비율을 [1, max]로 클램프해 기본점수에 곱함 */
  scoreSpeedBonusMax: number;
  /**
   * 책상 파괴. minMass 이상의 중량 물건(모니터/맥북)만 금 데칼·책상 타격 대상.
   * 세게 내리쳐(하향 속도 ≥ 물건 임계값) 물건이 깨질 때마다 1히트 — hits회째에 상판이
   * 반으로 쪼개진다. 중량 물건이 3개(모니터×2+맥북)이므로 hits=3이면 셋 다 내리쳐야 한다.
   */
  deskBreak: { minMass: number; hits: number };
  /**
   * 레이지 무브(한 방에 쓸어버리기 / 책상 뒤집기).
   * sweep.speed는 카탈로그 최대 임계값(모니터 9.5)보다 커야 벽/바닥 충돌로 전부 깨진다.
   * flip.up은 천장(아랫면 y≈3.0)에 잔여 속도 ≥ 2(천장 즉시 파괴 최소 속도)로 닿도록 잡는다.
   */
  rage: {
    /** 쓸어버리기 재사용 대기(초, 실시간 — 슬로모 무관). */
    sweepCooldownSec: number;
    /**
     * 쓸어버리기 배팅 — 팔이 지나간 물건에 팔 이동속도(armSpeed)에 비례한 속도를 전달한다.
     * 파괴는 직접 하지 않고 벽/바닥 충돌(DestructionSystem)에 맡긴다 → 살살 쓸면 살짝 밀리고,
     * 세게 쓸면 튕겨나가 깨진다.
     *  - transfer: armSpeed → 물건 수평 속도 배수.
     *  - liftRatio/maxLift: 리프트 = min(maxLift, 속도×liftRatio) — 속도에 비례해 뜬다(살살=거의 안 뜸).
     *  - lateral: z 산포. spin/spinRatio: 텀블 = min(spin, armSpeed×spinRatio).
     *  - minArmSpeed: 이하 팔 속도는 배팅하지 않음(정지/미세 이동 데드존).
     *  - maxArmSpeed: armSpeed 상한(극단적 플릭의 과속 방지 — CCD가 있지만 안전 클램프).
     *  - reach: 크로싱 구간 X 여유(손 두께의 절반) — 손 바로 옆 물건도 포착.
     */
    sweep: {
      transfer: number;
      liftRatio: number;
      maxLift: number;
      lateral: number;
      spin: number;
      spinRatio: number;
      minArmSpeed: number;
      maxArmSpeed: number;
      reach: number;
    };
    /** 책상 뒤집기 물건 발사: 상향 up+jitter, 좌우 산포 lateral, 뒤쪽 밀림 back, 회전 spin. */
    flip: { up: number; jitter: number; lateral: number; back: number; spin: number };
    /**
     * 책상 뒤집기 직후 슬로모 억제 창(초). 뒤집기는 물건을 공중에 쏟아 콤보가 3+로 치솟는데,
     * 그때 슬로모가 걸리면 역동적인 뒤집기 연출이 도로 느려진다 — 이 창 동안 슬로모를 막는다.
     */
    flipNoSlowmoSec: number;
    /** 제스처 임계값: flipDragY = 뒤집기 발동 위로 드래그(월드 m). 쓸기는 연속 배팅이라 임계값 없음. */
    gesture: { flipDragY: number };
  };
  /** 고정 물리 타임스텝(초) */
  fixedDt: number;
}

export const BALANCE: BalanceConfig = {
  // --- Clarifications 확정값 (계약) ---
  comboWindowSec: 2.5, // FR-006
  comboMultipliers: [1.0, 1.5, 2.0, 3.0], // FR-006, 4콤보+ 마지막 고정
  slowmo: { minCombo: 3, durationSec: 0.3, timeScale: 0.25 }, // FR-007
  debris: { globalCap: 120, lifetimeSec: 8 }, // FR-004b/c

  // --- 감각 상수 (플레이테스트 튜닝) ---
  throwImpulseScale: 1.15,
  // 파편이 더 능동적으로 튀도록 기본 임펄스·산포·상향 팝 강화 + 임팩트 세기에 비례 스케일.
  fragmentImpulse: { base: 3.6, jitter: 2.8, up: 2.2, speedRef: 7, speedScaleMax: 3.0 },
  scoreSpeedBonusMax: 2.5,
  // 중량 물건 3개(모니터×2 + 맥북)를 전부 세게 내리쳐야 책상이 부서진다.
  deskBreak: { minMass: 1.5, hits: 3 },
  rage: {
    sweepCooldownSec: 6,
    // transfer 1.2: 느린 팔(2 m/s)→2.4 m/s(최저 임계값 4 미만 = 안 부서짐),
    //              빠른 팔(10 m/s)→12 m/s(최고 임계값 9.5 초과 = 벽 충돌로 전부 깨짐).
    sweep: {
      transfer: 1.2, liftRatio: 0.18, maxLift: 2.5, lateral: 1.0,
      spin: 12, spinRatio: 1.6, minArmSpeed: 0.8, maxArmSpeed: 18, reach: 0.22,
    },
    flip: { up: 9.5, jitter: 2.5, lateral: 1.6, back: 1.2, spin: 9 },
    // 발사된 물건이 천장/바닥에 부딪혀 대부분 깨질 때까지(≈2초) 슬로모 억제.
    flipNoSlowmoSec: 2.0,
    gesture: { flipDragY: 0.45 },
  },
  fixedDt: 1 / 60,
};
